import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAgencyMembers } from "@/hooks/platform/useAgency";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";

const CommunicationsHub = () => {
  const { data: members = [], isLoading, error, refetch } = useAgencyMembers();
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const rows = useMemo(() => {
    const t = search.trim().toLowerCase();
    return members
      .filter((m) => m.user_type === "creator" || m.user_type === "venue")
      .filter((m) => (!t ? true : [m.username, m.full_name, m.portal_id].some((v) => (v ?? "").toLowerCase().includes(t))));
  }, [members, search]);

  if (isLoading) return <LoadingState label="Loading contacts…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-400">Reach any creator or venue on the roster directly.</p>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contact"
          className="w-64 bg-zinc-950 border-zinc-800 text-white"
        />
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={MessageSquare} title="No contacts" description="Members appear here once they join." />
      ) : (
        <div className="space-y-2">
          {rows.map((m) => (
            <Card key={m.id} className="bg-white/5 border-white/20">
              <CardContent className="p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                  <p className="font-medium text-white">{m.full_name || m.username}</p>
                  <p className="text-xs text-gray-400">
                    @{m.username} · <span className="font-mono">{m.portal_id ?? "—"}</span>
                  </p>
                </div>
                <Badge className="bg-white/10 border-0 capitalize">{m.user_type}</Badge>
                <Button size="sm" className="gap-2" onClick={() => navigate(`/messages?to=${m.id}`)}>
                  <Send className="h-3.5 w-3.5" /> Message
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunicationsHub;
